import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

const CreateJobModal = ({ isOpen, onClose, initialData = null, isEditMode = false, jobId = null }) => {
  const [templates, setTemplates] = useState([])
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mode: 'run_job',
    template_id: '',
    search_query: '',
    max_results: 50,
    urls: [],
    schedule: {
      frequency: 'daily',
      time: '08:00',
      days_of_week: [],
      day_of_month: 1,
      interval_hours: 1,
      cron_expression: ''
    },
    llm_enabled: true,
    llm_provider: 'openai',
    llm_model: 'gpt-4o-mini',
    llm_api_key: '',
    llm_extraction_prompt: '',
    cache_enabled: false,
    extraction_schema: null,
    advanced_options: null,
    notifications: {
      email_on_success: true,
      email_on_failure: true,
      webhook_url: ''
    }
  })

  const [selectedDays, setSelectedDays] = useState([])

  useEffect(() => {
    if (isOpen) {
      fetchTemplates()
      if (initialData) {
        // Merge initialData with default formData
        setFormData(prev => ({
          ...prev,
          ...initialData,
          schedule: initialData.schedule || prev.schedule,
          notifications: initialData.notifications || prev.notifications
        }))
        
        // Set selected days for weekly schedule
        if (initialData.schedule?.days_of_week) {
          setSelectedDays(initialData.schedule.days_of_week)
        }
      }
    } else {
      // Reset form when modal closes
      setFormData({
        name: '',
        description: '',
        mode: 'run_job',
        template_id: '',
        search_query: '',
        max_results: 50,
        urls: [],
        schedule: {
          frequency: 'daily',
          time: '08:00',
          days_of_week: [],
          day_of_month: 1,
          interval_hours: 1,
          cron_expression: ''
        },
        llm_enabled: true,
        llm_provider: 'openai',
        llm_model: 'gpt-4o-mini',
        llm_api_key: '',
        llm_extraction_prompt: '',
        cache_enabled: false,
        extraction_schema: null,
        advanced_options: null,
        notifications: {
          email_on_success: true,
          email_on_failure: true,
          webhook_url: ''
        }
      })
      setSelectedDays([])
    }
  }, [isOpen, initialData])

  const fetchTemplates = async () => {
    try {
      console.log('Fetching templates...')
      const response = await fetch('/api/templates/')
      const data = await response.json()
      console.log('Templates response:', data)
      
      // API can return { success: true, templates: [...] } or { total: X, templates: [...] }
      if (data.success && data.templates && Array.isArray(data.templates)) {
        setTemplates(data.templates)
        console.log('Templates loaded:', data.templates.length)
      } else if (data.templates && Array.isArray(data.templates)) {
        // Fallback if success field is missing
        setTemplates(data.templates)
        console.log('Templates loaded (no success field):', data.templates.length)
      } else {
        console.error('Unexpected templates response format:', data)
        setTemplates([])
        toast.error('Failed to load templates')
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
      toast.error('Failed to load templates')
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleScheduleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value
      }
    }))
  }

  const handleNotificationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value
      }
    }))
  }

  const toggleDay = (day) => {
    const newDays = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day]
    setSelectedDays(newDays)
    handleScheduleChange('days_of_week', newDays)
  }

  const handleSubmit = async () => {
    // Validation
    if (!formData.name) {
      toast.error('Please enter a job name')
      return
    }

    if (formData.mode === 'run_job' && !formData.template_id) {
      toast.error('Please select a template')
      return
    }

    if (formData.mode === 'auto' && !formData.search_query) {
      toast.error('Please enter a search query')
      return
    }

    try {
      // Use relative URL - nginx will proxy to backend
      const url = isEditMode 
        ? `/api/jobs/${jobId}`
        : '/api/jobs/'
      
      const method = isEditMode ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        toast.success(isEditMode ? 'Job updated successfully!' : 'Job created successfully!')
        onClose(true) // Pass true to indicate refresh needed
      } else {
        toast.error(data.message || `Failed to ${isEditMode ? 'update' : 'create'} job`)
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} job:`, error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} job`)
    }
  }

  if (!isOpen) return null

  const daysOfWeek = [
    { label: 'Mon', value: 0 },
    { label: 'Tue', value: 1 },
    { label: 'Wed', value: 2 },
    { label: 'Thu', value: 3 },
    { label: 'Fri', value: 4 },
    { label: 'Sat', value: 5 },
    { label: 'Sun', value: 6 }
  ]

  const getSchedulePreview = () => {
    const { frequency, time, days_of_week, day_of_month, interval_hours } = formData.schedule
    
    switch (frequency) {
      case 'hourly':
        return `Job will run every ${interval_hours} hour(s)`
      case 'daily':
        return `Job will run every day at ${time} UTC`
      case 'weekly':
        const dayNames = days_of_week?.map(d => daysOfWeek[d]?.label).join(', ') || 'No days selected'
        return `Job will run on ${dayNames} at ${time} UTC`
      case 'monthly':
        return `Job will run on day ${day_of_month} of each month at ${time} UTC`
      case 'custom':
        return 'Custom cron expression'
      default:
        return 'Invalid schedule'
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-8">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-light-200/20 px-8 py-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-dark-300">
            {isEditMode ? 'Edit Scheduled Job' : 'Create New Scheduled Job'}
          </h2>
          <button 
            onClick={() => onClose(false)}
            className="p-2 hover:bg-light-50 rounded-lg transition-colors"
          >
            <i className="fa-solid fa-times text-dark-100 text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          {/* Job Name */}
          <section>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">Job Name <span className="text-primary-500">*</span></span>
            </label>
            <input 
              type="text" 
              placeholder="e.g., Daily Jugendamt Extraction"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </section>

          {/* Description */}
          <section>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">Description</span>
            </label>
            <textarea 
              rows="2"
              placeholder="Describe what this job does..."
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </section>

          {/* Extraction Mode */}
          <section>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">Extraction Mode <span className="text-primary-500">*</span></span>
            </label>
            <select 
              value={formData.mode}
              onChange={(e) => handleInputChange('mode', e.target.value)}
              className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="run_job">Run Job (with Template)</option>
              <option value="auto">Auto Search</option>
              <option value="single">Single URL</option>
              <option value="batch">Batch URLs</option>
            </select>
          </section>

          {/* Template Selection (if run_job mode) */}
          {formData.mode === 'run_job' && (
            <section>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">Select Template <span className="text-primary-500">*</span></span>
              </label>
              <select 
                value={formData.template_id}
                onChange={(e) => handleInputChange('template_id', e.target.value)}
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Choose a template...</option>
                {templates.map(template => (
                  <option key={template._id} value={template._id}>
                    {template.name} ({template.schema ? Object.keys(template.schema).length : 0} fields)
                  </option>
                ))}
              </select>
            </section>
          )}

          {/* Search Query (if auto or run_job mode) */}
          {(formData.mode === 'auto' || formData.mode === 'run_job') && (
            <section>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">Search Query</span>
              </label>
              <textarea 
                rows="2"
                placeholder="e.g., Jugendamt Germany"
                value={formData.search_query}
                onChange={(e) => handleInputChange('search_query', e.target.value)}
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </section>
          )}

          {/* Schedule Configuration */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Schedule Configuration</h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Frequency <span className="text-primary-500">*</span></span>
                </label>
                <select 
                  value={formData.schedule.frequency}
                  onChange={(e) => handleScheduleChange('frequency', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom (Cron Expression)</option>
                </select>
              </div>

              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Time (UTC) <span className="text-primary-500">*</span></span>
                </label>
                <input 
                  type="time"
                  value={formData.schedule.time}
                  onChange={(e) => handleScheduleChange('time', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Hourly Interval */}
            {formData.schedule.frequency === 'hourly' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Interval (Hours)</span>
                </label>
                <input 
                  type="number"
                  min="1"
                  max="24"
                  value={formData.schedule.interval_hours}
                  onChange={(e) => handleScheduleChange('interval_hours', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Days of Week (for Weekly) */}
            {formData.schedule.frequency === 'weekly' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Days of Week</span>
                </label>
                <div className="flex space-x-2">
                  {daysOfWeek.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedDays.includes(day.value)
                          ? 'gradient-primary text-white'
                          : 'bg-light-100 hover:bg-light-200/30 text-dark-300'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Day of Month (for Monthly) */}
            {formData.schedule.frequency === 'monthly' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Day of Month (1-31)</span>
                </label>
                <input 
                  type="number"
                  min="1"
                  max="31"
                  value={formData.schedule.day_of_month}
                  onChange={(e) => handleScheduleChange('day_of_month', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Cron Expression (for Custom) */}
            {formData.schedule.frequency === 'custom' && (
              <div className="mb-4">
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Cron Expression</span>
                </label>
                <input 
                  type="text"
                  placeholder="0 0 * * *"
                  value={formData.schedule.cron_expression}
                  onChange={(e) => handleScheduleChange('cron_expression', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                />
                <p className="text-xs text-dark-100 mt-1">
                  <i className="fa-solid fa-info-circle mr-1"></i>
                  Format: minute hour day month weekday
                </p>
              </div>
            )}

            {/* Schedule Preview */}
            <div className="bg-primary-500/5 border-l-4 border-primary-500 rounded-r-lg p-4">
              <p className="text-sm text-dark-100">
                <i className="fa-solid fa-info-circle mr-2 text-primary-500"></i>
                <strong>Preview:</strong> {getSchedulePreview()}
              </p>
            </div>
          </section>

          {/* Extraction Configuration */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Extraction Configuration</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">Max Results</span>
                </label>
                <input 
                  type="number"
                  min="1"
                  max="200"
                  value={formData.max_results}
                  onChange={(e) => handleInputChange('max_results', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block mb-2">
                  <span className="text-sm font-bold text-dark-300">LLM Model</span>
                </label>
                <select 
                  value={formData.llm_model}
                  onChange={(e) => handleInputChange('llm_model', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="gpt-4o-mini">GPT-4o-mini (Fast & Cheap)</option>
                  <option value="gpt-4o">GPT-4o (Best Quality)</option>
                  <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                  <option value="claude-3-haiku-20240307">Claude 3 Haiku (Fast)</option>
                </select>
              </div>
            </div>
          </section>

          {/* Notifications */}
          <section className="bg-light-50 rounded-xl p-6 border border-light-200/20">
            <h3 className="text-lg font-bold text-dark-300 mb-4">Notifications & Alerts</h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-light-200/20">
                <div>
                  <div className="text-sm font-semibold text-dark-300">Email on Success</div>
                  <div className="text-xs text-dark-100">Send email when job completes successfully</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.notifications.email_on_success}
                    onChange={(e) => handleNotificationChange('email_on_success', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-light-200/20">
                <div>
                  <div className="text-sm font-semibold text-dark-300">Email on Failure</div>
                  <div className="text-xs text-dark-100">Send email when job fails</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.notifications.email_on_failure}
                    onChange={(e) => handleNotificationChange('email_on_failure', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="p-4 bg-white rounded-lg border border-light-200/20">
                <div className="mb-2">
                  <div className="text-sm font-semibold text-dark-300">Webhook URL (Optional)</div>
                  <div className="text-xs text-dark-100">Send webhook POST request after job completion</div>
                </div>
                <input 
                  type="url"
                  placeholder="https://your-webhook-url.com/endpoint"
                  value={formData.notifications.webhook_url}
                  onChange={(e) => handleNotificationChange('webhook_url', e.target.value)}
                  className="w-full px-4 py-2 border border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-light-200/20 px-8 py-6 flex items-center justify-end space-x-3">
          <button 
            onClick={() => onClose(false)}
            className="px-6 py-3 bg-light-100 hover:bg-light-200/30 rounded-lg font-semibold text-dark-300 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold transition-all shadow-md flex items-center"
          >
            <i className="fa-solid fa-check mr-2"></i>
            {isEditMode ? 'Update Job' : 'Create Job'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateJobModal

